import MuiCard from '@material-ui/core/Card';
import MuiCardContent from '@material-ui/core/CardContent';
import { bodySmall } from '@monorepo/shared/styles/text';
import * as importedTheme from '@monorepo/shared/themes/default';
import LightbulbIcon from '@svg/lightbulb.svg';
import WarningIcon from '@svg/warning.svg';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { ErrorIcon } from './errorDisplays/ErrorIcon';

export enum InfoCardVariant {
  ERROR = 'error',
  INFO = 'info',
  WARNING = 'warning',
}

const getColors = (variant: InfoCardVariant, theme: typeof importedTheme) => {
  switch (variant) {
    case InfoCardVariant.ERROR:
      return {
        backgroundColor: theme.colors.red08,
        textColor: theme.colors.errorContent,
        iconColor: theme.colors.darkRed,
      };
    case InfoCardVariant.INFO:
      return {
        backgroundColor: theme.colors.green08,
        textColor: theme.colors.gray666,
        iconColor: theme.colors.green,
      };
    case InfoCardVariant.WARNING:
      return {
        backgroundColor: theme.colors.warningFill,
        textColor: theme.colors.warningContent,
        iconColor: theme.colors.orange,
      };
    default: {
      const exhaustiveCheck: never = variant;
      throw new Error(`Unhandled variant: ${exhaustiveCheck}`);
    }
  }
};

const Card = styled(MuiCard)<{ $variant: InfoCardVariant }>`
  background-color: ${({ theme, $variant }) =>
    getColors($variant, theme).backgroundColor};
  box-shadow: none;
`;

const CardContent = styled(MuiCardContent)<{
  $variant: InfoCardVariant;
  $isCompact: boolean;
}>`
  ${bodySmall}
  display: flex;
  gap: 0.25rem;
  align-items: center;
  padding: ${({ $isCompact }) => ($isCompact ? '0.25rem 0.5rem' : '0.75rem')};
  color: ${({ theme, $variant }) => getColors($variant, theme).textColor};

  &:last-child {
    padding: ${({ $isCompact }) => ($isCompact ? '0.25rem 0.5rem' : '0.75rem')};
  }
`;

const IconContainer = styled.div<{
  $variant: InfoCardVariant;
  $isCompact: boolean;
}>`
  display: ${({ $isCompact }) => ($isCompact ? 'flex' : 'block')};
  padding: ${({ $isCompact }) => ($isCompact ? '0' : '0 0.5rem 0 0')};

  & svg {
    width: ${({ $isCompact }) => ($isCompact ? '1rem' : '1.375rem')};
    ${({ $isCompact }) => ($isCompact ? 'height: 1rem;' : '')}
    fill: ${({ theme, $variant }) => getColors($variant, theme).iconColor};
  }
`;

const TextContainer = styled.div`
  line-height: 1.5;
`;

type InfoCardProps = Omit<React.ComponentProps<typeof MuiCard>, 'variant'> & {
  children?: React.ReactNode;
  icon?: React.ReactNode;
  isCompact?: boolean;
  variant?: InfoCardVariant;
};

export function InfoCard({
  children,
  icon: iconProp,
  isCompact = false,
  variant = InfoCardVariant.INFO,
  ...props
}: InfoCardProps) {
  const icon = useMemo(() => {
    if (iconProp) {
      return iconProp;
    }
    if (variant === InfoCardVariant.WARNING) {
      return <WarningIcon />;
    }
    if (variant === InfoCardVariant.ERROR) {
      return <ErrorIcon />;
    }
    return <LightbulbIcon />;
  }, [iconProp, variant]);

  return (
    <Card $variant={variant} {...props}>
      <CardContent $variant={variant} $isCompact={isCompact}>
        <IconContainer $variant={variant} $isCompact={isCompact}>
          {icon}
        </IconContainer>
        <TextContainer>{children}</TextContainer>
      </CardContent>
    </Card>
  );
}
